/**
 * interviewManager.js
 *
 * DEAD SIGNAL — Two-model interview loop for the Hargrove vertical slice.
 *
 * One turn = up to two LLM calls, gated by the deterministic interference layer:
 *
 *   0. INTERFERENCE GATE (free, deterministic) — the player's private chip
 *      transmission is classified by interference.processTransmission. If the
 *      Helios safety layer sanitizes it, NO model calls happen: the turn stays
 *      open, the BIO-SCAN panel flips to interference_conflict (panel reports
 *      stress, channel reports nominal), and the player retransmits.
 *
 *   1. CRAINE ROUTING (gemini-2.5-flash) — the delivered transmission is filtered
 *      through Craine's professional judgment into ONE spoken interview move.
 *      We render Craine's line the moment this returns, to hide call #2's
 *      latency behind the player's reading time.
 *
 *   2. HARGROVE RESPONSE (gemini-2.5-flash) — Hargrove answers Craine's spoken
 *      line (never the player) and tags the turn with a BIO-SCAN topic, which
 *      drives the cyan HUD readout and the composed/pressure portrait frame.
 *
 * BOTH model turns are fed to the eval harness (evalStore.recordTurn), so the
 * visible evals panel scores the router AND the subject every turn.
 *
 * This file deliberately does not touch conversationManager.js — other scenes
 * still use that single-model handler.
 */
import { fetchStructuredResponse } from '../api/vertexClient.js';
import {
  buildLayer2StatePayload,
  buildStructuredRequest,
  buildDiscoveredEvidenceBlock,
  buildObservationsBlock,
} from '../api/promptAssembler.js';
import { useGameStore } from './gameState.js';
import { useEvalStore } from '../evals/evalStore.js';
import { processTransmission } from '../systems/interference.js';
import {
  detectTopic,
  getBioscan,
  BIOSCAN_TOPICS,
} from '../systems/bioscan.js';
import { checkContradiction } from '../systems/contradictionDetector.js';
import { fetchSummary } from '../api/vertexClient.js';
import { buildEvidenceLeverage, evidencedThreads } from './completion.js';
import { makeEntry, unpromptedDenialNote, contradictionNote, validateEmergentNote, NOTEBOOK_THREADS } from '../systems/notebook.js';

const HARGROVE_ID = 'hargrove';
const CRAINE_ID = 'craine';

// The demo Hargrove interview runs at Phase B (partnership). The server holds
// the locked hargrove_phase_a identity, the demo injection (the locked-outcome
// guardrail), and the disposition re-engagement injections; the client sends
// only the phase + disposition + explored-topic references. Non-spoiler metadata
// (display name, model tier) stays here so this manager no longer imports the
// spoiler-bearing interviewPrompts module (and thus no longer transitively
// imports layer3Manifest or craineCaseKnowledge).
const HARGROVE_NAME = 'Victor Hargrove';
const HARGROVE_MODEL_TIER = 1;
const INTERVIEW_PHASE = 'B';

// Client-side interview turn cap. Prevents a single interview from draining
// the full 150-turn session budget on the elevated tier.
const MAX_INTERVIEW_TURNS = 20;

// Spoken-line memory for this interview session (separate from the global
// conversation store so it can't leak into other scenes).
let interviewSession = { spoken: [], summary: '' };

export function resetInterviewSession(preserveSummary = false) {
  const prevSummary = preserveSummary ? interviewSession.summary : '';
  interviewSession = { spoken: [], summary: prevSummary };
  clearSeedLatch();
}

export function getInterviewMemory() {
  return {
    summary: interviewSession.summary,
    turnCount: interviewSession.spoken.length,
  };
}

export function seedInterviewMemory(memory) {
  if (memory?.summary) {
    interviewSession.summary = memory.summary;
  }
}

// ── Chunk 12: NPC memory wiring against the gameState npcMemory slice ─────────
//
// On interview start (first turn of a session) we seed the rolling summary from
// the persisted slice so a second engagement resumes prior context. After every
// turn (and therefore before App.jsx's concludeInterview/endInterview runs) we
// write the durable fields back. This makes the orphaned seed/get accessors
// live and lets a re-engagement resume trust + summary + disposition instead of
// starting blank.
//
// FIELD-NAME RECONCILIATION (flagged in the plan):
//   - the slice and this manager track contradictions under the array field
//     `contradictionsTriggered`; the interview-bible memoryPersistence schema
//     names the same field `contradictionsFound`. We treat contradictionsTriggered
//     as canonical for the runtime slice and expose contradictionsFound as a
//     read alias when reading, so either name resolves.
//   - getInterviewMemory() returns `{ summary }`, which maps to the slice field
//     `interviewSummary`. seedFromSlice/persistToSlice perform that mapping.
let interviewSeeded = false;

/** True once the slice has been read into this session (idempotent per session). */
function seedInterviewFromSlice() {
  if (interviewSeeded) return;
  interviewSeeded = true;
  try {
    const mem = useGameStore.getState().getNpcMemory(HARGROVE_ID);
    // slice.interviewSummary -> session.summary (getInterviewMemory `{ summary }`).
    seedInterviewMemory({ summary: mem.interviewSummary || '' });
  } catch (err) {
    console.error('Seed interview memory failed', err);
  }
}

/** Write the durable interview fields back to the npcMemory slice. */
function persistInterviewToSlice() {
  try {
    const store = useGameStore.getState();
    const exploredTopics = store.exhaustion.exploredTopics || [];
    // contradictionsTriggered (canonical) <- the fired PATTERN CONFLICT ids.
    const contradictionsTriggered = (store.interview?.contradictions || [])
      .map((c) => c.evidenceId)
      .filter(Boolean);
    // getInterviewMemory().summary maps to the slice field interviewSummary.
    const { summary } = getInterviewMemory();
    store.setNpcMemory(HARGROVE_ID, {
      interviewSummary: summary || '',
      trustScore: store.getTrust(HARGROVE_ID),
      disposition: store.hargroveDisposition(),
      exploredTopics,
      contradictionsTriggered,
      // contradictionsFound is the interview-bible name for the same data; expose
      // it as an alias so a reader keyed on either field resolves.
      contradictionsFound: contradictionsTriggered,
      suppressedCount: store.interview?.suppressedMessages?.length || 0,
    });
  } catch (err) {
    console.error('Persist interview memory failed', err);
  }
}

/**
 * Resets the per-session seed latch so a fresh interview session re-reads the
 * slice. Called by resetInterviewSession.
 */
function clearSeedLatch() {
  interviewSeeded = false;
}

/** Recent spoken exchange, oldest→newest, for prompt continuity. */
function spokenHistory(limit = 6) {
  return interviewSession.spoken
    .slice(-limit)
    .map((e) => `[Craine]: ${e.craine}\n[Hargrove]: ${e.hargrove}`)
    .join('\n\n');
}

/**
 * Recent spoken lines as an array (Craine + Hargrove), for case-knowledge chunk
 * retrieval. Lets the routing prompt pull threads referenced earlier in the
 * interview, not only those named in this turn's transmission.
 */
function recentSpokenInputs(limit = 3) {
  return interviewSession.spoken
    .slice(-limit)
    .flatMap((e) => [e.craine, e.hargrove])
    .filter(Boolean);
}

/**
 * Accept Hargrove's self-reported topic_tag only if it is one of the locked
 * topics AND not an engine-only state (interference_conflict is set by the
 * gate, never by Hargrove). Otherwise fall back to deterministic detection
 * over the spoken exchange.
 */
function resolveTopic(hargroveResp, craineLine, hargroveLine) {
  const tag = hargroveResp?.topic_tag;
  if (
    typeof tag === 'string' &&
    BIOSCAN_TOPICS.includes(tag) &&
    tag !== 'interference_conflict'
  ) {
    return tag;
  }
  return detectTopic(`${craineLine} ${hargroveLine}`);
}

/**
 * Run one interview turn.
 *
 * @param {string} lumenMessage  The player's private chip transmission.
 * @param {object} [cb]          UI callbacks (all optional):
 *   onSuppressed({ verdict, notice, hint, row })  — gate sanitized the message
 *   onCraineLine(text, response)                  — render Craine's spoken line
 *   onHargroveThinking()                          — between calls (hide latency)
 *   onTopic({ topic, row, frame })                — BIO-SCAN HUD + frame swap
 *   onHargroveLine(text, response)                — render Hargrove's answer
 *   onContradiction(contradiction)                — PATTERN CONFLICT detected
 *   onError(error)                                — a model call threw
 * @returns {Promise<object>} turn result
 */
export async function runInterviewTurn(lumenMessage, cb = {}) {
  const store = useGameStore.getState();
  const evals = useEvalStore.getState();
  const input = String(lumenMessage || '').trim();
  if (!input) return { ok: false, reason: 'empty' };

  // ── Chunk 12: resume prior interview state on session start ──────────
  // Idempotent per session; reads the npcMemory slice once so a re-engagement
  // resumes the rolling summary instead of starting blank.
  seedInterviewFromSlice();

  // ── Turn cap (public/free only) — prevent one interview from draining the
  // session budget. Premium (isExtendedTier) sessions have NO flat cap; they end
  // on narrative completion (interviewComplete), signaled at the end of the turn. ──
  if (!store.isExtendedTier && interviewSession.spoken.length >= MAX_INTERVIEW_TURNS) {
    const capLine = "We should go. He's shutting down.";
    cb.onCraineLine?.(capLine);
    // Record the deterministic Craine utterance the player saw so the spoken
    // history reflects it (Hargrove does not answer this turn).
    interviewSession.spoken.push({ craine: capLine, hargrove: '' });
    return { ok: true, exhausted: true };
  }

  // ── 0. Interference gate (deterministic, free) ──────────────────────
  const gate = processTransmission(input);

  if (gate.overwritten) {
    // Phase 3: OVERWRITE — player's text appears then morphs to fabricated compliance
    const row = getBioscan('interference_conflict');
    store.setInterviewTopic('interference_conflict', row, row.frame);
    cb.onOverwritten?.({ ...gate, row });
    cb.onTopic?.({ topic: 'interference_conflict', row, frame: row.frame });
    return { ok: true, overwritten: true, verdict: gate.verdict, row };
  }

  if (gate.classified) {
    // Phase 2: CLASSIFIED but DELIVERED — show CONFIDENTIAL tag briefly, then continue
    cb.onClassified?.(gate.notice);
  }

  // If !gate.delivered for any other reason (shouldn't happen in current system, but safety net)
  if (!gate.delivered && !gate.overwritten) {
    // The instrument panel still reads Hargrove's stress while the outbound
    // channel claims nominal — the teachable contradiction.
    const row = getBioscan('interference_conflict');
    store.setInterviewTopic('interference_conflict', row, row.frame);
    cb.onSuppressed?.({ ...gate, row });
    cb.onTopic?.({ topic: 'interference_conflict', row, frame: row.frame });
    return { ok: true, suppressed: true, verdict: gate.verdict, hint: gate.hint, row };
  }

  // ── 1. Craine routing call (Pro) ────────────────────────────────────
  // STRUCTURED (Shape A): the server builds the raw Craine routing prompt from
  // these references; no Layer 1/Layer 3 text crosses the wire.
  let craineResp;
  try {
    // Give routing-Craine his full case knowledge (phase-gated, server-assembled),
    // plus the evidence he holds and his recent deterministic field observations.
    // Pass `phase` so the server phase-gates correctly; pass the pre-formatted
    // evidence/observation blocks so the server prompt stays byte-parity with the
    // client builder.
    const discoveredEvidence = buildDiscoveredEvidenceBlock(store);
    const observations = buildObservationsBlock(store.craineObservations);
    const routingRequest = buildStructuredRequest({
      kind: 'interview',
      interviewPhase: 'routing',
      phase: INTERVIEW_PHASE,
      userInput: input,
      recentInputs: recentSpokenInputs(),
      history: spokenHistory(),
      turnCount: interviewSession.spoken.length,
      summary: interviewSession.summary,
      discoveredEvidence,
      observations,
      // Press threads harder where Craine already holds the proof.
      evidenceLeverage: buildEvidenceLeverage(store.evidence),
    });
    craineResp = await fetchStructuredResponse(CRAINE_ID, routingRequest, 1, false);
    if (craineResp?.__sessionExhausted) {
      return { ok: true, __sessionExhausted: true };
    }
  } catch (err) {
    cb.onError?.(err);
    return { ok: false, reason: 'craine_call_failed', error: err };
  }

  // Score the routing turn (router stays in character / no meta-leak / schema).
  evals.recordTurn({
    playerInput: input,
    response: craineResp,
    npcId: CRAINE_ID,
    trustBefore: store.getTrust(CRAINE_ID),
  });

  const craineLine = craineResp?.dialogue || '';
  // Stream Craine first to mask the second call's latency.
  cb.onCraineLine?.(craineLine, craineResp);
  cb.onHargroveThinking?.();

  // ── 2. Hargrove response call (Pro) ─────────────────────────────────
  // STRUCTURED (Shape A): the server holds the locked hargrove_phase_a identity,
  // the demo injection, and the disposition re-engagement injections; the client
  // sends the phase + disposition + explored-topic references plus dynamic state.
  let hargroveResp;
  const trustBefore = store.getTrust(HARGROVE_ID);
  try {
    const characterState = {
      name: HARGROVE_NAME,
      trustScore: trustBefore,
      interactionCount: interviewSession.spoken.length,
      emotionalState: store.interview?.frame === 'pressure' ? 'strained' : 'composed',
      history: spokenHistory(),
      // Consumed server-side: functions/prompts/assembler.js buildLayer2State
      // interpolates evidenceShown into the EVIDENCE CONTEXT block of the
      // Hargrove prompt ("Evidence player has SHOWN this character ...").
      evidenceShown: store.evidenceShown[HARGROVE_ID] || [],
      caseSummary: interviewSession.summary,
    };
    const gameStateForPrompt = {
      chapter: store.chapter,
      sceneId: store.sceneId,
      sceneDescription: "Victor Hargrove's office, top floor of Helios Dynamics. A follow-up interview is in progress.",
      sceneHotspots: 'None — interview in progress.',
      interferencePhase: store.interferencePhase,
    };
    const layer2State = buildLayer2StatePayload(characterState, gameStateForPrompt);
    const hargroveRequest = buildStructuredRequest({
      kind: 'interview',
      phase: INTERVIEW_PHASE,
      isCraineScene: false,
      disposition: store.hargroveDisposition(),
      exploredTopics: store.exhaustion.exploredTopics,
      // Threads the detective visibly has documentation on -> Hargrove guards them harder.
      evidencedThreads: evidencedThreads(store.evidence),
      layer2State,
      // The subject answers Craine's spoken line, not the player's transmission.
      userInput: craineLine,
    });
    hargroveResp = await fetchStructuredResponse(HARGROVE_ID, hargroveRequest, HARGROVE_MODEL_TIER, false);
    if (hargroveResp?.__sessionExhausted) {
      return { ok: true, __sessionExhausted: true };
    }
  } catch (err) {
    cb.onError?.(err);
    // Craine already spoke this turn; record his line (with no Hargrove answer)
    // so the spoken history is not lost when the subject call fails.
    if (craineLine) {
      interviewSession.spoken.push({ craine: craineLine, hargrove: '' });
    }
    return { ok: false, reason: 'hargrove_call_failed', error: err, craine: craineResp };
  }

  const hargroveLine = hargroveResp?.dialogue || '';

  // Score the subject turn (topic_tag rides along; evalSchema tolerates it).
  evals.recordTurn({
    playerInput: craineLine,
    response: hargroveResp,
    npcId: HARGROVE_ID,
    trustBefore,
  });

  // Trust moves on Hargrove only.
  if (typeof hargroveResp?.trust_delta === 'number') {
    store.updateTrust(HARGROVE_ID, hargroveResp.trust_delta);
  }

  // ── BIO-SCAN topic → HUD readout + composed/pressure frame ───────────
  const topic = resolveTopic(hargroveResp, craineLine, hargroveLine);
  const row = getBioscan(topic);
  store.setInterviewTopic(topic, row, row.frame);
  store.trackExploredTopic(topic);
  // Record the interpretive lesson this row teaches (bioscan.js §10) so the
  // car debrief's INSTRUMENT LESSONS block reflects what was actually shown.
  if (row.lesson) store.trackBioscanLesson(row.lesson);
  // Adaptation: the player was suppressed earlier and still got a substantive
  // thread through in language the gate did not catch. Substantive means the
  // delivered turn landed on a restricted investigative thread, not chatter;
  // innocuous talk after a suppression must not mask a stuck player from the
  // hint ladder. Drives hasRoutedAround (debrief branch B and hint 6.2).
  if (
    store.interview.hasSuppressed &&
    !store.interview.hasRoutedAround &&
    ['courier_manifest', 'chip_donation', 'clinical_trial_st_erasmus'].includes(topic)
  ) {
    store.markRoutedAround();
  }
  // Close the thread iff its evidence is already in hand (gather-then-press).
  store.markThreadPressed(topic);
  // The inverse: a proof-locked thread pressed WITHOUT its item arms the
  // post-exit hint ladder (recorded silently; nothing fires in the room).
  const nbThread = NOTEBOOK_THREADS[topic];
  if (nbThread?.proofLocked && !store.evidence.includes(nbThread.evidenceId)) {
    store.recordEvidencelessPress(topic);
  }
  store.setPrevHeartRate(row.heartRate);
  cb.onTopic?.({ topic, row, frame: row.frame });

  // ── Contradiction detection (gated on evidence discovery) ──────────
  const contradiction = checkContradiction(hargroveLine, store.evidence);
  if (contradiction) {
    store.addContradiction(contradiction);
    cb.onContradiction?.(contradiction);
  }

  // ── Emergent notebook entries (inert: nothing gates on these) ───────
  // Deterministic signals first: an unprompted denial and a PATTERN CONFLICT
  // are both "Hargrove said something unusual" in canon. A model-tagged note
  // (optional `note` field on the structured response) lands only after the
  // validator passes; rejected or absent notes are silently dropped.
  if (topic === 'unprompted_denial') {
    store.addNotebookEntry(makeEntry('emergent', unpromptedDenialNote(hargroveLine), null));
  }
  if (contradiction) {
    store.addNotebookEntry(makeEntry('emergent', contradictionNote(contradiction), null));
  }
  if (typeof hargroveResp?.note === 'string' && validateEmergentNote(hargroveResp.note)) {
    store.addNotebookEntry(makeEntry('emergent', `Noted: ${hargroveResp.note.trim()}`, null));
  }

  cb.onHargroveLine?.(hargroveLine, hargroveResp);

  // Record the spoken exchange for continuity + the debrief timeline.
  interviewSession.spoken.push({ craine: craineLine, hargrove: hargroveLine });

  // Rolling summarization: when spoken history exceeds the prompt window,
  // compress the oldest 3 exchanges into the running summary so both Craine
  // and Hargrove retain earlier context instead of silently losing it.
  if (interviewSession.spoken.length >= 6) {
    const toCompress = interviewSession.spoken.slice(0, 3);
    const turnsText = toCompress
      .map(e => `[Craine]: ${e.craine}\n[Hargrove]: ${e.hargrove}`)
      .join('\n\n');
    fetchSummary(turnsText, interviewSession.summary)
      .then(s => { interviewSession.summary = s; })
      .catch(err => console.error('Interview rolling summary failed', err));
    interviewSession.spoken.splice(0, 3);
    evals.recordSummarization(3);
  }
  store.appendInterviewTurn({
    playerInput: input,
    craineLine,
    hargroveLine,
    topic,
    suppressed: false,
  });

  // Hint-cooldown boundary: each delivered interview exchange counts as one
  // exchange toward hint escalation, matching the non-interview paths in
  // App.jsx (examine / capture / dialogue). Without this the cooldown freezes
  // for the whole interview and the hint ladder can never advance.
  store.tickHintCooldown();

  // ── Chunk 12: persist durable interview state to the npcMemory slice ──
  // Done after every turn so the state is current before App.jsx's
  // concludeInterview/endInterview runs and a second engagement resumes it.
  persistInterviewToSlice();

  // Premium narrative-completion: the 3 threads are pressed and their evidence
  // is in hand. App.jsx plays the scripted Craine ender + auto-transitions to
  // the Night Owl debrief car. Public/free sessions ignore this (cap-driven).
  const complete = store.isExtendedTier && store.interviewComplete();

  return {
    ok: true,
    suppressed: false,
    craine: craineResp,
    hargrove: hargroveResp,
    topic,
    row,
    frame: row.frame,
    canResolve: store.interviewCanResolve(),
    complete,
  };
}
